import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  Alert,
  Divider,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Autocomplete,
  TextField,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Business as BusinessIcon,
  DeliveryDining as CourierIcon,
  Restaurant as RestaurantIcon,
  ShoppingCart as ECommerceIcon,
  Inventory as PackageIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { mainApi } from '../../config/apiClient';
import { useTranslation } from 'react-i18next';
import GuideButton from '../common/guide/GuideButton';

interface Branch {
  id: string;
  name: string;
}

interface CourierOrderSettingsModalProps {
  open: boolean;
  onClose: () => void;
  courier: {
    id: number;
    firebaseUid: string;
    name: string;
    branchIds?: string;
    branchNames?: string;
  } | null;
}

const CourierOrderSettingsModal: React.FC<CourierOrderSettingsModalProps> = ({
  open,
  onClose,
  courier
}) => {
  const { t } = useTranslation('salesSystem');
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { branches, userDetails } = useAuth();
  const companyId = userDetails?.companyId;
  
  const [loading, setLoading] = useState(false);
  const [selectedBranches, setSelectedBranches] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Sipariş türü ayarları
  const [restaurantOrders, setRestaurantOrders] = useState(true);
  const [ecommerceOrders, setEcommerceOrders] = useState(true);
  const [packageOrders, setPackageOrders] = useState(true);

  // Şubeleri ve mevcut ayarları yükle
  useEffect(() => {
    const loadBranchesAndSettings = async () => {
      if (!companyId || !courier) return;
      
      setLoading(true);
      try {
        // ✅ Şubeler artık AuthContext'ten otomatik geliyor

        // SQL'den mevcut kurye ayarlarını çek
        try {
            // ✅ MULTI-TENANT: companyId parametresi ekle
            const response = await mainApi.get(
              `/api/couriers/firebase/${courier.firebaseUid}?companyId=${companyId}`
            );

            if (response.data.success && response.data.data) {
              console.log('✅ Mevcut kurye ayarları yüklendi:', response.data.data);
              
              const data = response.data.data;
              
              // Şubeleri ayarla - SQL'den gelen branchIds virgülle ayrılmış string
              if (data.branchIds) {
                const branchArray = typeof data.branchIds === 'string' 
                  ? data.branchIds.split(',').filter((id: string) => id.trim())
                  : data.branchIds;
                setSelectedBranches(branchArray);
              } else if (courier.branchIds) {
                const branchArray = courier.branchIds.split(',').filter(id => id.trim());
                setSelectedBranches(branchArray);
              }
              
              // Sipariş türlerini ayarla
              setRestaurantOrders(data.restaurantOrders ?? true);
              setEcommerceOrders(data.ecommerceOrders ?? true);
              setPackageOrders(data.packageOrders ?? true);
            } else if (courier.branchIds) {
              // SQL'de kayıt yoksa mevcut değerleri kullan
              const branchArray = courier.branchIds.split(',').filter(id => id.trim());
              setSelectedBranches(branchArray);
            }
          } catch (apiError) {
            console.warn('⚠️ SQL ayarları yüklenemedi, varsayılan değerler kullanılıyor:', apiError);
            if (courier.branchIds) {
              const branchArray = courier.branchIds.split(',').filter(id => id.trim());
              setSelectedBranches(branchArray);
            }
          }
      } catch (error) {
        console.error('❌ Şubeler ve ayarlar yüklenirken hata:', error);
        setError(t('courierOrderSettingsModal.errors.couldNotLoadInfo'));
      } finally {
        setLoading(false);
      }
    };
    
    if (companyId && courier) {
      loadBranchesAndSettings();
    }
  }, [companyId, courier]);

  // Modal kapandığında state'leri sıfırla
  useEffect(() => {
    if (!open) {
      setSelectedBranches([]);
      // ✅ branches artık AuthContext'ten geliyor, reset'e gerek yok
      setError(null);
      setRestaurantOrders(true);
      setEcommerceOrders(true);
      setPackageOrders(true);
    }
  }, [open]);

  // Şube ekleme - Autocomplete'den seçildiğinde
  const handleAddBranch = (branch: Branch | null) => {
    if (branch && !selectedBranches.includes(branch.id)) {
      setSelectedBranches([...selectedBranches, branch.id]);
    }
  };

  // Şube kaldırma
  const handleRemoveBranch = (branchId: string) => {
    setSelectedBranches(selectedBranches.filter(id => id !== branchId));
  };

  const handleSave = async () => {
    if (!courier || !companyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('💾 Kurye sipariş ayarları kaydediliyor:', {
        courierId: courier.id,
        firebaseUid: courier.firebaseUid,
        selectedBranches,
        restaurantOrders,
        ecommerceOrders,
        packageOrders
      });

      // SQL'e sipariş ayarlarını kaydet
      const orderSettings = {
        branchIds: selectedBranches,
        restaurantOrders: restaurantOrders,
        ecommerceOrders: ecommerceOrders,
        packageOrders: packageOrders
      };

      console.log('📤 SQL güncelleme isteği gönderiliyor...', {
        firebaseUid: courier.firebaseUid,
        orderSettings
      });

      const response = await mainApi.post(
        `/api/couriers/update-order-settings`,
        {
          firebaseUid: courier.firebaseUid,
          companyId: companyId,
          branchIds: selectedBranches,
          restaurantOrders: restaurantOrders,
          ecommerceOrders: ecommerceOrders,
          packageOrders: packageOrders,
          orderSettings: orderSettings
        }
      );

      console.log('📥 SQL güncelleme yanıtı:', {
        status: response.status,
        data: response.data
      });

      if (response.data.success) {
        console.log('✅ Kurye sipariş ayarları başarıyla güncellendi');
        onClose();
      } else {
        throw new Error(response.data.message || t('courierOrderSettingsModal.errors.updateFailed'));
      }
    } catch (error) {
      console.error('❌ Sipariş ayarları güncelleme hatası:', error);
      
      let errorMessage = t('courierOrderSettingsModal.errors.updateOrderSettings');
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    // Değişiklikleri geri al
    if (courier?.branchIds) {
      const branchArray = courier.branchIds.split(',').filter(id => id.trim());
      setSelectedBranches(branchArray);
    } else {
      setSelectedBranches([]);
    }
    setRestaurantOrders(true);
    setEcommerceOrders(true);
    setPackageOrders(true);
    onClose();
  };

  // Seçilmemiş şubeleri filtrele (autocomplete için)
  const availableBranches = branches.filter(branch => !selectedBranches.includes(branch.id));

  if (!courier) return null;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: '1px solid #eee',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="h6" fontWeight="bold">
              {t('courierOrderSettingsModal.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('courierOrderSettingsModal.subtitle', { name: courier.name })}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <GuideButton guideKey="couriers.orderSettings" />
          <IconButton edge="end" color="inherit" onClick={handleCancel}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Kurye Bilgileri */}
          <Grid item xs={12}>
            <Card sx={{ bgcolor: 'primary.50', borderRadius: 2 }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CourierIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" fontWeight="bold">
                      {courier.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('courierOrderSettingsModal.currentBranch')}: {courier.branchNames || t('courierOrderSettingsModal.noBranchAssigned')}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Şube Seçimi */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon color="primary" />
              {t('courierOrderSettingsModal.assignedBranches.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('courierOrderSettingsModal.assignedBranches.description')}
            </Typography>
            
            {/* Autocomplete Şube Filtresi */}
            <Autocomplete
              options={availableBranches}
              getOptionLabel={(option) => option.name}
              onChange={(event, value) => handleAddBranch(value)}
              value={null}
              disabled={loading || availableBranches.length === 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('courierOrderSettingsModal.assignedBranches.searchAddLabel')}
                  placeholder={t('courierOrderSettingsModal.assignedBranches.searchPlaceholder')}
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <AddIcon sx={{ color: 'text.secondary', mr: 0.5 }} />,
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon fontSize="small" color="primary" />
                    <Typography>{option.name}</Typography>
                  </Box>
                </li>
              )}
              noOptionsText={t('courierOrderSettingsModal.assignedBranches.allBranchesSelected')}
              sx={{ mb: 2 }}
            />
            
            {/* Seçilen Şubeler */}
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                minHeight: 80,
                bgcolor: selectedBranches.length > 0 ? 'success.50' : 'grey.50',
                border: selectedBranches.length > 0 ? '1px solid' : '1px dashed',
                borderColor: selectedBranches.length > 0 ? 'success.200' : 'grey.300',
                borderRadius: 2
              }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                {t('courierOrderSettingsModal.assignedBranches.selectedCount', { count: selectedBranches.length })}
              </Typography>
              
              {selectedBranches.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                  {t('courierOrderSettingsModal.assignedBranches.noneSelected')}
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedBranches.map((branchId) => {
                    const branch = branches.find(b => b.id === branchId);
                    return (
                      <Chip 
                        key={branchId} 
                        label={branch?.name || t('courierOrderSettingsModal.assignedBranches.unknownBranch')} 
                        onDelete={() => handleRemoveBranch(branchId)}
                        color="success"
                        variant="filled"
                        icon={<BusinessIcon />}
                        sx={{ 
                          fontWeight: 'medium',
                          '& .MuiChip-deleteIcon': {
                            color: 'success.dark',
                            '&:hover': { color: 'error.main' }
                          }
                        }}
                      />
                    );
                  })}
                </Box>
              )}
            </Paper>
            
            {branches.length === 0 && !loading && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {t('courierOrderSettingsModal.assignedBranches.noBranchesFound')}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Sipariş Türü Ayarları */}
          <Grid item xs={12}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {t('courierOrderSettingsModal.orderTypes.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('courierOrderSettingsModal.orderTypes.description')}
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Card sx={{ border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={restaurantOrders}
                        onChange={(e) => setRestaurantOrders(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <RestaurantIcon color={restaurantOrders ? 'primary' : 'disabled'} />
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {t('courierOrderSettingsModal.orderTypes.restaurant.title')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('courierOrderSettingsModal.orderTypes.restaurant.description')}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>

              <Card sx={{ border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={ecommerceOrders}
                        onChange={(e) => setEcommerceOrders(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ECommerceIcon color={ecommerceOrders ? 'primary' : 'disabled'} />
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {t('courierOrderSettingsModal.orderTypes.ecommerce.title')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('courierOrderSettingsModal.orderTypes.ecommerce.description')}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>

              <Card sx={{ border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ p: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={packageOrders}
                        onChange={(e) => setPackageOrders(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PackageIcon color={packageOrders ? 'primary' : 'disabled'} />
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {t('courierOrderSettingsModal.orderTypes.package.title')}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('courierOrderSettingsModal.orderTypes.package.description')}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #eee', gap: 1 }}>
        <Button 
          onClick={handleCancel}
          variant="outlined"
          disabled={loading}
        >
          {t('courierOrderSettingsModal.actions.cancel')}
        </Button>
        <Button 
          onClick={handleSave}
          variant="contained"
          disabled={loading || selectedBranches.length === 0}
          sx={{ minWidth: 120 }}
        >
          {loading ? t('courierOrderSettingsModal.actions.saving') : t('courierOrderSettingsModal.actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourierOrderSettingsModal;
